import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Eye, EyeOff, Save, User, Key, Palette, ExternalLink, Shield, CreditCard, CheckCircle, XCircle } from 'lucide-react'
import api from '../utils/api'
import useAuthStore from '../stores/authStore'
import useThemeStore from '../stores/themeStore'
import Card from '../components/ui/Card'
import Button from '../components/ui/Button'
import Input from '../components/ui/Input'
import TMSProjectManager from '../components/tms/TMSProjectManager'

const SettingsPage = () => {
  const { t } = useTranslation()
  const { user } = useAuthStore()
  const { isDark, toggleTheme } = useThemeStore()
  const queryClient = useQueryClient()
  
  const [showCurrentPassword, setShowCurrentPassword] = useState(false)
  const [showNewPassword, setShowNewPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  })
  
  
  const [errors, setErrors] = useState({})
  const [success, setSuccess] = useState('')

  // Ensure theme is properly applied when component mounts
  useEffect(() => {
    const { initializeTheme } = useThemeStore.getState()
    initializeTheme()
  }, [])

  // Fetch user settings
  const { data: settings = {} } = useQuery({
    queryKey: ['user-settings'],
    queryFn: async () => {
      const response = await api.get('/user/settings')
      return response.data
    },
  })

  // Fetch account information including subscription
  const { data: accountData = {} } = useQuery({
    queryKey: ['user-account'],
    queryFn: async () => {
      const response = await api.get('/user/account')
      return response.data
    },
  })

  // Update password mutation
  const passwordMutation = useMutation({
    mutationFn: async (data) => {
      const response = await api.put('/user/password', {
        current_password: data.currentPassword,
        new_password: data.newPassword,
      })
      return response.data
    },
    onSuccess: () => {
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' })
      setSuccess(t('validation.passwordUpdated'))
      setErrors({})
      setTimeout(() => setSuccess(''), 3000)
    },
    onError: (error) => {
      setErrors({ password: error.response?.data?.detail || t('validation.passwordUpdateFailed') })
    },
  })


  const handlePasswordSubmit = (e) => {
    e.preventDefault()
    setErrors({})
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      setErrors({ password: t('validation.allPasswordFields') })
      return
    }
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setErrors({ password: t('validation.passwordsNoMatch') })
      return
    }
    
    if (passwordData.newPassword.length < 8) {
      setErrors({ password: t('validation.passwordMinLength') })
      return
    }
    
    passwordMutation.mutate(passwordData)
  }


  return (
    <div className="page-transition space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          {t('settings.title')}
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Success Message */}
      {success && (
        <div className="bg-green-50 dark:bg-green-900/50 border border-green-200 dark:border-green-800 rounded-lg p-4">
          <p className="text-sm text-green-600 dark:text-green-400">{success}</p>
        </div>
      )}

      {/* Account Information - Full Width */}
      <Card>
        <Card.Header>
          <div className="flex items-center space-x-2">
            <User className="w-5 h-5 text-gray-600 dark:text-gray-400" />
            <Card.Title>{t('account.information')}</Card.Title>
          </div>
        </Card.Header>
        <Card.Content>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Basic Account Info */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.emailAddress')}
                </label>
                <Input
                  type="email"
                  value={accountData.user?.email || user?.email || ''}
                  disabled
                  className="bg-gray-50 dark:bg-gray-700"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  {t('account.emailNote')}
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.type')}
                </label>
                <Input
                  value={accountData.user?.role || user?.role || 'user'}
                  disabled
                  className="bg-gray-50 dark:bg-gray-700 capitalize"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.memberSince')}
                </label>
                <Input
                  value={accountData.user?.memberSince ? new Date(accountData.user.memberSince).toLocaleDateString() : 
                         user?.created_at ? new Date(user.created_at).toLocaleDateString() : t('account.na')}
                  disabled
                  className="bg-gray-50 dark:bg-gray-700"
                />
              </div>
            </div>

            {/* Subscription Information */}
            <div className="space-y-4">
              <div className="flex items-center space-x-2 mb-4">
                <CreditCard className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">{t('account.subscriptionInfo')}</h3>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.currentPlan')}
                </label>
                <Input
                  value={t(`account.${accountData.subscription?.planName || 'free'}`)}
                  disabled
                  className="bg-gray-50 dark:bg-gray-700"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.planStatus')}
                </label>
                <div className="flex items-center space-x-2">
                  <Input
                    value={t(`account.${accountData.subscription?.planStatus || 'active'}`)}
                    disabled
                    className="bg-gray-50 dark:bg-gray-700 capitalize"
                  />
                  {accountData.subscription?.planStatus === 'active' ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : (
                    <XCircle className="w-5 h-5 text-red-500" />
                  )}
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.characterUsage')}
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                  <div className="flex justify-between text-sm mb-2">
                    <span>{t('account.charactersUsed')}</span>
                    <span>
                      {accountData.subscription?.usage?.charactersUsed?.toLocaleString() || '0'} / {' '}
                      {accountData.subscription?.usage?.monthlyLimit?.toLocaleString() || '10,000'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                    <div 
                      className="bg-blue-600 h-2 rounded-full" 
                      style={{width: `${Math.min(accountData.subscription?.usage?.percentageUsed || 0, 100)}%`}}
                    ></div>
                  </div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {Math.round(accountData.subscription?.usage?.percentageUsed || 0)}{t('account.percentageUsed')}
                  </p>
                </div>
              </div>
              
              {/* Plan Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  {t('account.subscriptionFeatures')}
                </label>
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t('account.prioritySupport')}</span>
                    {accountData.subscription?.features?.prioritySupport ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t('account.phraseIntegration')}</span>
                    {accountData.subscription?.features?.phraseIntegration ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm">{t('account.qualityReview')}</span>
                    {accountData.subscription?.features?.qualityReview ? (
                      <CheckCircle className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-gray-400" />
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card.Content>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Change Password */}
        <Card>
          <Card.Header>
            <div className="flex items-center space-x-2">
              <Shield className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <Card.Title>{t('password.change')}</Card.Title>
            </div>
          </Card.Header>
          <Card.Content>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              {errors.password && (
                <div className="bg-red-50 dark:bg-red-900/50 border border-red-200 dark:border-red-800 rounded-lg p-3">
                  <p className="text-sm text-red-600 dark:text-red-400">{errors.password}</p>
                </div>
              )}
              
              <div className="relative">
                <Input
                  label={t('password.current')}
                  type={showCurrentPassword ? 'text' : 'password'}
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                  placeholder={t('password.enterCurrent')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="relative">
                <Input
                  label={t('password.new')}
                  type={showNewPassword ? 'text' : 'password'}
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, newPassword: e.target.value }))}
                  placeholder={t('password.enterNew')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <div className="relative">
                <Input
                  label={t('password.confirmNew')}
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  placeholder={t('password.confirmNewPlaceholder')}
                />
                <button
                  type="button"
                  className="absolute right-3 top-8 text-gray-400 hover:text-gray-600"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>

              <Button
                type="submit"
                loading={passwordMutation.isPending}
                disabled={passwordMutation.isPending}
                className="w-full"
              >
                <Save className="w-4 h-4 mr-2" />
                {t('password.update')}
              </Button>
            </form>
          </Card.Content>
        </Card>

        {/* Theme Settings */}
        <Card>
          <Card.Header>
            <div className="flex items-center space-x-2">
              <Palette className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <Card.Title>{t('theme.appearance')}</Card.Title>
            </div>
          </Card.Header>
          <Card.Content className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-900 dark:text-white">
                  {t('theme.darkMode')}
                </p>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {t('theme.toggleDescription')}
                </p>
              </div>
              <button
                onClick={toggleTheme}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  isDark ? 'bg-primary-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    isDark ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </Card.Content>
        </Card>

        {/* TMS Integration - Multi-Project Management */}
        <Card>
          <Card.Header>
            <div className="flex items-center space-x-2">
              <Key className="w-5 h-5 text-gray-600 dark:text-gray-400" />
              <Card.Title>{t('phrase.integration')}</Card.Title>
            </div>
          </Card.Header>
          <Card.Content>
            <TMSProjectManager />
          </Card.Content>
        </Card>

      </div>
    </div>
  )
}

export default SettingsPage